import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetHrAndBillingContactsMutation } from "../../../../api/services/client/clientApis";
import { Item as CountryListItems } from "../../../../api/services/client/models";
import { NisParticipantResponse } from "../../../../api/services/nisParticipants/models";
import {
  useGetClientListByCountry,
  useGetProjectManagersListMutation,
} from "../../../../api/services/nisParticipants/nisParticipantsApis";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  FormController,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import {
  MAX_LENGTH_100,
  PAGE_IDS,
  REGEX_ALLOW_ALPHA_NUMERIC_WITHOUT_SPECIAL_CHAR,
  ROLE_IDS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { UserDetails } from "../../../../helpers/types/common";
import {
  emailValidation,
  formatApiDataToDropDownOption,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { NISParticipantStatus } from "../../../programSearch/modelsAndSchemas/models";
import {
  CLIENT_SERVICES_EMAIL,
  CreateNISParticipantFormKey,
  GEO_SCOPE_NORTH_AMERICA,
  OTHER_DROPDOWN_FOR_HR_CONTACT,
  OTHER_DROPDOWN_ITEM,
  VALUE_OTHER,
} from "../../modelsAndSchemas/Models";
import { createNISParticipantProfessionalDetailsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProfessionalDetailsWrapper } from "./CreateProfessionalDetails.style";

const pageId = PAGE_IDS.CREATE_NIS_PARTICIPANT_PROFESSIONAL_DETAILS;

interface CreateProfessionalDetailsProps {
  isEdit?: boolean;
  countryList: OptionsDataType[];
  rawCountryListData: CountryListItems[];
  editNISParticipantData?: NisParticipantResponse;
}

export const CreateProfessionalDetails: FC<CreateProfessionalDetailsProps> = ({
  isEdit,
  countryList,
  rawCountryListData,
  editNISParticipantData,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { useFetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const dispatch = useDispatch();
  const { commonARIALabel } = useNavigationSiteCore();
  const [
    dedicatedProjectManagerMappedData,
    setDedicatedProjectManagerMappedData,
  ] = useState<OptionsDataType[]>([]);

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const clientEmployerWatch = useWatch({
    control,
    name: CreateNISParticipantFormKey.clientEmployer,
  });
  const hrContactWatch = useWatch({
    control,
    name: CreateNISParticipantFormKey.hrContact,
  });
  const countryWatch = useWatch({
    control,
    name: CreateNISParticipantFormKey.country,
  });

  const isClientEmployerOtherSelected = useMemo(
    () => clientEmployerWatch === VALUE_OTHER,
    [clientEmployerWatch],
  );

  const isHRContactOtherSelected = useMemo(
    () => hrContactWatch === VALUE_OTHER,
    [hrContactWatch],
  );

  const isEmployeeIdDisabled = useMemo(() => {
    return (
      isEdit &&
      (editNISParticipantData?.profile?.statusId ===
        NISParticipantStatus.Converted ||
        editNISParticipantData?.profile?.statusId ===
          NISParticipantStatus.Ineligible)
    );
  }, [isEdit, editNISParticipantData]);

  const {
    mutateAsync: getProjectManagersList,
    data: projectManagerList,
    isSuccess: isProjectManagerApiSuccess,
    isPending: isProjectManagerApiPending,
  } = useGetProjectManagersListMutation();

  const {
    mutateAsync: getProjectManagersByRole,
    data: projectManagerByRoleData,
    isSuccess: isProjectManagerByRoleApiSuccess,
    isPending: isProjectManagerByRoleApiPending,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getHrContacts,
    data: hrContacts,
    isPending: isHrContactsPending,
  } = useGetHrAndBillingContactsMutation();

  const {
    data: clientList,
    isFetching: isClientListLoading,
    refetchWithParams: getClientListByCountry,
  } = useGetClientListByCountry();

  useEffect(() => {
    dispatch(
      toggleLoader({
        nisDataFetching: {
          isVisible:
            isProjectManagerApiPending ||
            isProjectManagerByRoleApiPending ||
            isHrContactsPending ||
            isClientListLoading,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          nisDataFetching: {
            isVisible: false,
          },
        }),
      );
    };
  }, [
    isHrContactsPending,
    isProjectManagerApiPending,
    isProjectManagerByRoleApiPending,
    isClientListLoading,
  ]);

  const resetHrContactFields = () => {
    setValue(CreateNISParticipantFormKey.hrContact, "");
    setValue(CreateNISParticipantFormKey.hrContactNameOther, "");
    setValue(CreateNISParticipantFormKey.hrContactEmailIdOther, "");
  };

  const resetProjectManagerFields = () => {
    setDedicatedProjectManagerMappedData([]);
    setValue(CreateNISParticipantFormKey.projectManagerId, "");
  };

  const getProjectManagersByRoleApi = () => {
    getProjectManagersByRole({ roleIds: [ROLE_IDS.projectManager] });
  };

  useEffect(() => {
    if (isEmpty(clientEmployerWatch)) {
      return;
    }

    if (isClientEmployerOtherSelected) {
      getProjectManagersByRoleApi();
      setValue(CreateNISParticipantFormKey.hrContact, VALUE_OTHER);
    } else {
      getHrContacts({ clientId: clientEmployerWatch, contactType: "hr" });
      getProjectManagersList({ clientId: clientEmployerWatch });
    }
  }, [clientEmployerWatch]);

  const isSelectedCountryInNorthAmerica = useMemo(() => {
    if (isNotEmpty(countryWatch) && isNotEmpty(rawCountryListData)) {
      const selectedCountry = rawCountryListData.find(
        (item) => item.key === countryWatch,
      );
      return selectedCountry?.geoScopeName === GEO_SCOPE_NORTH_AMERICA;
    }
    return false;
  }, [countryWatch, rawCountryListData]);

  useEffect(() => {
    if (isNotEmpty(countryWatch)) {
      getClientListByCountry({
        countryCode: countryWatch,
        isActiveClientOnly: true,
      });
    }
  }, [countryWatch]);

  const handleClientEmployerChange = (selectedValue?: string) => {
    setValue(CreateNISParticipantFormKey.clientEmployerOther, "");
    resetHrContactFields();
    resetProjectManagerFields();

    if (isEmpty(selectedValue)) {
      return;
    }
  };

  const handleCountryChange = () => {
    setValue(CreateNISParticipantFormKey.clientEmployer, "");
  };

  const setApiDataToPMDropdown = (projectManagerData: UserDetails[]) => {
    const mappedData = formatApiDataToDropDownOption(projectManagerData);
    setDedicatedProjectManagerMappedData(mappedData);
  };

  const setSharedServiceIdsToPMDropdown = () => {
    setDedicatedProjectManagerMappedData([
      {
        label: CLIENT_SERVICES_EMAIL,
        value: CLIENT_SERVICES_EMAIL,
      },
    ]);
  };

  useEffect(() => {
    if (!isProjectManagerApiSuccess) {
      return;
    }

    if (isNotEmpty(projectManagerList)) {
      setApiDataToPMDropdown(projectManagerList);
      return;
    }

    if (isSelectedCountryInNorthAmerica) {
      setSharedServiceIdsToPMDropdown();
      return;
    }

    getProjectManagersByRoleApi();
  }, [
    isProjectManagerApiSuccess,
    projectManagerList,
    isSelectedCountryInNorthAmerica,
  ]);

  useEffect(() => {
    if (!isProjectManagerByRoleApiSuccess) return;

    if (isNotEmpty(projectManagerByRoleData)) {
      setApiDataToPMDropdown(projectManagerByRoleData);
      return;
    }

    if (isSelectedCountryInNorthAmerica) {
      setSharedServiceIdsToPMDropdown();
      return;
    }

    setDedicatedProjectManagerMappedData([]);
  }, [isProjectManagerByRoleApiSuccess, projectManagerByRoleData]);

  const {
    professionalDetailsTitle,
    country: countryLabel,
    clientEmployer,
    clientEmployerOther,
    employeeId,
    releaseDate,
    hrContact,
    hrContactEmailIdOther,
    hrContactNameOther,
    projectManager,
    severanceStatusSigned,
    severanceStatusList,
  } = createNISParticipantProfessionalDetailsSiteCoreKeys;
  const getLabel = useFetchLabel(professionalDetailsTitle);

  const getDateValue = (key: keyof typeof CreateNISParticipantFormKey) => {
    if (isEmpty(getValues(key))) {
      return null;
    }
    const value = getValues(key) as unknown as string;
    return dayjs(value, userCountryDateFormat);
  };

  const allowOnlyAlphaNumeric = {
    onInput: (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      target.value = target.value.replace(
        REGEX_ALLOW_ALPHA_NUMERIC_WITHOUT_SPECIAL_CHAR,
        "",
      );
    },
  };

  return (
    <CreateProfessionalDetailsWrapper>
      <MuiGrid className="main-container">
        <MuiGrid container>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.country}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.country}`}
                isMandatory
                label={getLabel(countryLabel)}
                visibleDefaultSelectOption={true}
                placeholder={commonLabels.selectPlaceHolder}
                onChange={() => handleCountryChange()}
                options={countryList}
                requiredARIALabel={commonARIALabel.required}
                showSearch
                searchPlaceholder={commonLabels.search}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.clientEmployer}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.clientEmployer}`}
                isMandatory
                label={getLabel(clientEmployer)}
                visibleDefaultSelectOption={true}
                placeholder={commonLabels.selectPlaceHolder}
                options={setApiDataToDropDownOption([
                  ...(clientList ?? []),
                  OTHER_DROPDOWN_ITEM,
                ])}
                onChange={(e) =>
                  handleClientEmployerChange(e.target.value as string)
                }
                requiredARIALabel={commonARIALabel.required}
                showSearch
                searchPlaceholder={commonLabels.search}
              />
            </FormController>
          </MuiGrid>
          {isClientEmployerOtherSelected && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateNISParticipantFormKey.clientEmployerOther}
              >
                <MuiTextField
                  id={`${pageId}-${CreateNISParticipantFormKey.clientEmployerOther}`}
                  label={getLabel(clientEmployerOther)}
                  isMandatory={isClientEmployerOtherSelected}
                  placeholder={commonLabels.inputPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  maxLength={MAX_LENGTH_100}
                />
              </FormController>
            </MuiGrid>
          )}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.projectManagerId}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.projectManagerId}`}
                label={getLabel(projectManager)}
                visibleDefaultSelectOption
                showSearch
                isMandatory
                options={dedicatedProjectManagerMappedData}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.employeeId}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.employeeId}`}
                label={getLabel(employeeId)}
                placeholder={commonLabels.inputPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                disabled={isEmployeeIdDisabled}
                inputProps={allowOnlyAlphaNumeric}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateFieldFormController
              control={control}
              name={CreateNISParticipantFormKey.releaseDate}
              id={`${pageId}-${CreateNISParticipantFormKey.releaseDate}`}
              value={getDateValue(CreateNISParticipantFormKey.releaseDate)}
              label={getLabel(releaseDate)}
              valueFormat={userCountryDateFormat}
              clearable
              className="date-picker"
              format={userCountryDateFormat}
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
          {!isClientEmployerOtherSelected && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <></>
            </MuiGrid>
          )}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.hrContact}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.hrContact}`}
                label={getLabel(hrContact)}
                visibleDefaultSelectOption
                placeholder={commonLabels.selectPlaceHolder}
                showSearch
                disabled={isClientEmployerOtherSelected}
                options={formatApiDataToDropDownOption(
                  [...(hrContacts ?? []), OTHER_DROPDOWN_FOR_HR_CONTACT],
                  true,
                )}
                isInfoIconVisible={false}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          {isHRContactOtherSelected && (
            <>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={CreateNISParticipantFormKey.hrContactNameOther}
                >
                  <MuiTextField
                    id={`${pageId}-${CreateNISParticipantFormKey.hrContactNameOther}`}
                    label={getLabel(hrContactNameOther)}
                    placeholder={commonLabels.inputPlaceHolder}
                    maxLength={MAX_LENGTH_100}
                    showCloseIcon
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={CreateNISParticipantFormKey.hrContactEmailIdOther}
                >
                  <MuiTextField
                    id={`${pageId}-${CreateNISParticipantFormKey.hrContactEmailIdOther}`}
                    label={getLabel(hrContactEmailIdOther)}
                    placeholder={commonLabels.emailPlaceHolder}
                    slotProps={{
                      htmlInput: {
                        ...emailValidation,
                        "aria-invalid": !!errors.hrContactEmailIdOther,
                      },
                    }}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </>
          )}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.severanceStatusSigned}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.severanceStatusSigned}`}
                label={getLabel(severanceStatusSigned)}
                visibleDefaultSelectOption={false}
                placeholder={commonLabels.selectPlaceHolder}
                showSearch
                options={fetchDropdownValues(
                  professionalDetailsTitle,
                  severanceStatusList,
                )}
                isInfoIconVisible={false}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </CreateProfessionalDetailsWrapper>
  );
};
